import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { AuthoringDoc } from './doc.js';
import { findNode } from './tree.js';

enablePatches();

// SessionCtx — Plasmic's `studioCtx.change()` discipline in ~30 lines, without MobX
// (mobile-native-v1.md, The editor). EVERY mutation funnels through `applyChange`, which runs it
// under immer and keeps the INVERSE patch. That is the entire undo system: no command objects, no
// action log, no snapshots of the doc.
//
// Vanilla zustand on purpose — this package is headless. `apps/web` binds it with `useStore`.

/** Capped so a long session cannot grow the stack without bound. 200 is generous: it is ~200
 *  inspector edits, and nobody undoes further than that. */
const UNDO_LIMIT = 200;

interface PatchPair {
  patches: Patch[];
  inverse: Patch[];
}

export interface SessionState {
  doc: AuthoringDoc;
  /** Block id, or null. NOT undoable — undo restores document state, not where you were looking. */
  selection: string | null;
  focusedStepId: string;
  undoStack: PatchPair[];
  redoStack: PatchPair[];
  /** What the top undo entry is coalescing. Internal; see `applyChange`. */
  mergeKey: string | null;

  /** `mergeKey` COALESCES consecutive changes into one undo entry. Without it, typing a headline
   *  into the inspector pushes one entry per keystroke and ⌘Z undoes a single letter. Callers pass
   *  `${nodeId}.${field}`, so moving to another field — or another node — breaks the chain by
   *  itself, and nothing has to remember to "commit" anything. */
  applyChange: (mutate: (draft: AuthoringDoc) => void, mergeKey?: string) => void;
  undo: () => void;
  redo: () => void;
  select: (id: string | null) => void;
  focusStep: (id: string) => void;
}

export type SessionStore = StoreApi<SessionState>;

export function createSession(doc: AuthoringDoc): SessionStore {
  const firstStep = doc.steps[0];
  if (!firstStep) throw new Error('createSession: a doc must have at least one step');

  return createStore<SessionState>((set, get) => ({
    doc,
    selection: null,
    focusedStepId: firstStep.id,
    undoStack: [],
    redoStack: [],
    mergeKey: null,

    applyChange: (mutate, mergeKey) => {
      const state = get();
      const [next, patches, inverse] = produceWithPatches(state.doc, mutate);

      // A change that changed nothing must not push an undo entry — otherwise ⌘Z becomes a game of
      // "how many no-ops did the inspector emit while I was clicking around".
      if (patches.length === 0) return;

      const top = state.undoStack[state.undoStack.length - 1];
      const coalesce = mergeKey !== undefined && mergeKey === state.mergeKey && top !== undefined;

      // The INVERSE concatenates in REVERSE (`inverse` then `top.inverse`) — undoing a coalesced
      // run must replay the individual undos newest-first, or an insert-then-edit merges into a
      // pair that restores the edit onto a node the first inverse already removed.
      const entry: PatchPair = coalesce
        ? { patches: [...top.patches, ...patches], inverse: [...inverse, ...top.inverse] }
        : { patches, inverse };
      const stack = coalesce ? state.undoStack.slice(0, -1) : state.undoStack;

      set({
        doc: next,
        undoStack: [...stack, entry].slice(-UNDO_LIMIT),
        redoStack: [],
        mergeKey: mergeKey ?? null,
        // Deleting the selected node — or the focused step — through applyChange leaves both
        // pointing at something that no longer exists. `stepToExperienceDocument` THROWS on a
        // missing step, so this is the canvas crashing, not a cosmetic glitch.
        ...reconcile(next, state),
      });
    },

    undo: () => {
      const { doc, undoStack, redoStack } = get();
      const entry = undoStack[undoStack.length - 1];
      if (!entry) return;
      const next = applyPatches(doc, entry.inverse);
      set({
        doc: next,
        undoStack: undoStack.slice(0, -1),
        redoStack: [...redoStack, entry],
        mergeKey: null,
        ...reconcile(next, get()),
      });
    },

    redo: () => {
      const { doc, undoStack, redoStack } = get();
      const entry = redoStack[redoStack.length - 1];
      if (!entry) return;
      const next = applyPatches(doc, entry.patches);
      set({
        doc: next,
        undoStack: [...undoStack, entry].slice(-UNDO_LIMIT),
        redoStack: redoStack.slice(0, -1),
        mergeKey: null,
        ...reconcile(next, get()),
      });
    },

    // Both break a coalescing run: an edit to the same field of the same node, either side of a
    // selection change, is two separate things the author did.
    select: (id) => set({ selection: id, mergeKey: null }),
    focusStep: (id) => set({ focusedStepId: id, selection: null, mergeKey: null }),
  }));
}

/** After an undo/redo the selected node or the focused step may no longer exist — undoing an
 *  "insert" deletes the very node the insert selected. A dangling selection is a crash in the
 *  inspector, so it is pruned here rather than defended against in every consumer. */
function reconcile(
  doc: AuthoringDoc,
  state: Pick<SessionState, 'selection' | 'focusedStepId'>
): Pick<SessionState, 'selection' | 'focusedStepId'> {
  const step =
    doc.steps.find((s) => s.id === state.focusedStepId) ?? (doc.steps[0] as AuthoringDoc['steps'][number]);
  const selectionExists =
    state.selection !== null && findNode(step.root, state.selection) !== undefined;

  return {
    focusedStepId: step.id,
    selection: selectionExists ? state.selection : null,
  };
}
